/**
 * Mobile Form Responses Table Component
 * 
 * Features:
 * - Card-based layout for mobile
 * - Touch-friendly interactions
 * - Checkbox selection per card
 * - Header delete icon for bulk delete
 * - Individual delete per card
 * - Responsive stacking
 */

import React, { useMemo } from 'react';
import { AlertCircle, Trash2 } from 'lucide-react';
import { formatResponseForTable } from '../utils/formResponseFormatters';

const MobileFormResponsesTable = ({
    responses = [],
    loading = false,
    error = null,
    onRowClick = null,
    propertiesMap = {},
    portfolioName = '',
    // Selection props
    selectedIds = [],
    onSelectionChange = null,
    onBulkDelete = null,
    onSingleDelete = null,
}) => {
    // Format responses for display with source name context
    const formatContext = { propertiesMap, portfolioName };
    const formattedResponses = useMemo(
        () => responses.map(r => formatResponseForTable(r, formatContext)),
        [responses, propertiesMap, portfolioName]
    );

    // Selection helpers
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allSelected = formattedResponses.length > 0 && formattedResponses.every(r => selectedSet.has(r.id));
    const someSelected = formattedResponses.some(r => selectedSet.has(r.id));

    const handleSelectAll = () => {
        if (!onSelectionChange) return;
        const visibleIds = formattedResponses.map(r => r.id);
        if (allSelected) {
            onSelectionChange(selectedIds.filter(id => !visibleIds.includes(id)));
        } else {
            const merged = new Set([...selectedIds, ...visibleIds]);
            onSelectionChange([...merged]);
        }
    };

    const handleToggle = (id) => {
        if (!onSelectionChange) return;
        if (selectedSet.has(id)) {
            onSelectionChange(selectedIds.filter(i => i !== id));
        } else {
            onSelectionChange([...selectedIds, id]);
        }
    };

    // Handle row click
    const handleRowClick = (response) => {
        if (onRowClick) {
            onRowClick(response);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="space-y-3">
                <div className="p-6 text-center">
                    <div className="inline-block">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-stone-700 mx-auto mb-2"></div>
                        <p className="text-xs text-stone-600">Loading responses...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                    <p className="text-xs font-medium text-red-900">Error loading responses</p>
                    <p className="text-[10px] text-red-700 mt-1">{error}</p>
                </div>
            </div>
        );
    }

    // Empty state
    if (formattedResponses.length === 0) {
        return (
            <div className="bg-white border border-notion-border rounded-lg p-8 text-center">
                <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                </div>
                <p className="text-sm font-medium text-stone-900">No responses yet</p>
                <p className="text-xs text-stone-500 mt-1">Submissions will appear here</p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Select All + Bulk Delete header */}
            {formattedResponses.length > 0 && (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-notion-border bg-white">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={allSelected}
                            ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                            onChange={handleSelectAll}
                            className="rounded border-stone-300 text-stone-800 focus:ring-stone-500"
                        />
                        <span className="text-xs font-medium text-stone-600">
                            {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all'}
                        </span>
                    </label>
                    <button
                        onClick={onBulkDelete}
                        disabled={selectedIds.length === 0}
                        title={selectedIds.length === 0 ? 'Select responses to delete' : `Delete ${selectedIds.length} selected`}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded transition-colors ${selectedIds.length > 0
                                ? 'text-red-600 hover:bg-red-50'
                                : 'text-stone-300 cursor-not-allowed'
                            }`}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            )}

            {formattedResponses.map((response) => (
                <div
                    key={response.id}
                    className={`bg-white border border-notion-border rounded-lg p-4 space-y-3 ${onRowClick ? 'cursor-pointer active:bg-stone-50' : ''
                        } ${selectedSet.has(response.id) ? 'ring-1 ring-stone-400' : ''} transition-colors`}
                >
                    {/* Header: Checkbox + Name + Delete */}
                    <div className="flex items-start gap-3">
                        <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={selectedSet.has(response.id)}
                                onChange={() => handleToggle(response.id)}
                                className="rounded border-stone-300 text-stone-800 focus:ring-stone-500 cursor-pointer"
                            />
                        </div>
                        <div className="flex-1 min-w-0" onClick={() => handleRowClick(response)}>
                            <h3 className="text-sm font-semibold text-notion-text truncate">{response.name}</h3>
                            <p className="text-xs text-stone-500 mt-1 truncate">{response.email}</p>
                        </div>
                        <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                            <button
                                onClick={() => onSingleDelete?.(response.id)}
                                title="Delete response"
                                className="inline-flex items-center justify-center w-7 h-7 rounded text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>

                    {/* Source + Time */}
                    <div className="flex justify-between items-center text-xs" onClick={() => handleRowClick(response)}>
                        <span className="text-stone-600">{response.source}</span>
                        <span className="text-stone-400">{response.submittedAtFormatted}</span>
                    </div>

                    {/* Footer: Phone if available */}
                    {response.phone && (
                        <div className="text-xs text-stone-500 border-t border-stone-100 pt-2 truncate">
                            <a href={`tel:${response.phone}`} className="text-blue-600 hover:underline">
                                {response.phone}
                            </a>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default MobileFormResponsesTable;
